// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { Download as DownloadIcon } from '@/constants/hardfin-standard-icons';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Typography,
} from '@mui/material';
import { DateTime } from 'luxon';
import { useCallback } from 'react';

interface TemplateDownloadProps {
  onBack: () => void;
  onNext: () => void;
}

export function TemplateDownload(props: TemplateDownloadProps) {
  const handleDownloadTemplate = useCallback(() => {
    const headers = [
      'Date',
      'Description',
      'Amount',
      'Currency (Ex USD)',
      'Person',
      'Project',
      'Category',
    ];

    const csvContent = headers.join(',') + '\n';

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute(
      'download',
      `cost_data_template_${DateTime.now().toFormat('yyyy-MM-dd_HHmm')}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, []);

  return (
    <Card variant={'outlined'}>
      <CardContent>
        <Box
          sx={{
            px: 3,
            py: 2,
            backgroundColor: (theme) => theme.palette.grey.A100,
            borderRadius: '10px',
            marginBottom: '10px',
          }}
        >
          <Typography variant="subtitle1" gutterBottom>
            Template for cost data
          </Typography>
          <Typography variant="subtitle2" gutterBottom>
            Required
          </Typography>
          <ul style={{ paddingLeft: '24px', marginTop: '8px' }}>
            <li>
              <Typography variant="body2">
                Date - date (YYYY-MM-DD format)
              </Typography>
            </li>
            <li>
              <Typography variant="body2">Description - text</Typography>
            </li>
            <li>
              <Typography variant="body2">
                Amount - number (ex: 150.75)
              </Typography>
            </li>
          </ul>
          <Typography variant="subtitle2" gutterBottom>
            Optional fields
          </Typography>
          <ul style={{ paddingLeft: '24px', marginTop: 0, marginBottom: 0 }}>
            <li>
              <Typography variant="body2">
                Currency - ISO code (ex: USD); defaults to your organization
                currency if blank
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                Person - email address or person's name (must be unique in your
                organization)
              </Typography>
            </li>
            <li>
              <Typography variant="body2">Project - project name</Typography>
            </li>
            <li>
              <Typography variant="body2">Category - text</Typography>
            </li>
          </ul>
        </Box>
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>Important information about the template</AlertTitle>
          <ul style={{ paddingLeft: '24px', marginTop: '8px' }}>
            <li>
              Date format must be YYYY-MM-DD (ex: '2020-06-25' for June 25,
              2020)
            </li>
            <li>
              Amount should be in currency format (ex: 123.45); more than two
              decimal places (ex: 987.6543) is rejected, while fewer (ex: 10.1)
              is padded to 10.10
            </li>
            <li>
              Person must match an existing name or email address from your
              organization
            </li>
            <li>Project must match an existing project name</li>
            <li>Optional fields do not need to be included</li>
          </ul>
        </Alert>

        <Box display="flex" justifyContent="space-between" mt={2}>
          <Button variant="outlined" onClick={props.onBack}>
            Close
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
          >
            Download template
          </Button>
          <Button variant="contained" onClick={props.onNext}>
            Next
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
